import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks";
import {
  updateManyProductFilterOptions,
  updateProductFilterOption,
} from "@/lib/redux/productFilters/productFiltersSlice";
import {
  productFilterOptionSelectors,
  productFilterSelectors,
} from "@/lib/redux/store";
import { useCallback, useMemo } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { CSSTransitionProps } from "react-transition-group/CSSTransition";
import { Button, Container } from "./styles";
import { productFilterValueToLabel } from "@/lib/helpers";
import { useRouter } from "next/router";
import { useTranslation } from "@/lib/translations";

const transitionProps: CSSTransitionProps = {
  timeout: 200,
  classNames: "item",
  unmountOnExit: true,
  mountOnEnter: true,
};

const Facets: React.FC = () => {
  const { locale = "it" } = useRouter();

  const t = {
    removeAll: useTranslation("filters.removeAll", locale) || "",
  };
  const dispatch = useAppDispatch();

  const filters = useAppSelector((state) =>
    productFilterSelectors
      .selectAll(state)
      .filter((filter) => filter.id !== "catalog")
      .map((filter) => ({
        ...filter,
        options: productFilterOptionSelectors
          .selectAll(filter)
          .filter((option) => option.selected),
      }))
  );

  const selectedOptionsTotal = useMemo(() => {
    let total = 0;
    filters.forEach((filter) => {
      total += filter.options.length;
    });
    return total;
  }, [filters]);

  const unselectFilterOption = useCallback(
    (filterId: string, optionId: string) => {
      dispatch(
        updateProductFilterOption({
          filterId,
          optionId,
          changes: {
            selected: false,
          },
        })
      );
    },
    [dispatch]
  );

  const unselectAllFilterOptions = useCallback(() => {
    filters.forEach((filter) => {
      dispatch(
        updateManyProductFilterOptions({
          filterId: filter.id,
          updates: filter.options.map((option) => ({
            id: option.value,
            changes: {
              selected: false,
            },
          })),
        })
      );
    });
  }, [dispatch, filters]);

  return (
    <Container $filtersActive={selectedOptionsTotal > 0}>
      <TransitionGroup component={null}>
        {filters.map((filter) => {
          return filter.options.map((option) => (
            <CSSTransition
              key={`${filter.id}:${option.value}`}
              {...transitionProps}
            >
              <Button
                onClick={() => unselectFilterOption(filter.id, option.value)}
              >
                {productFilterValueToLabel(option.value)}
              </Button>
            </CSSTransition>
          ));
        })}
        {selectedOptionsTotal > 0 && (
          <CSSTransition key={`remove-all`} {...transitionProps}>
            <Button
              onClick={() => unselectAllFilterOptions()}
              colorScheme="light"
            >
              {t.removeAll}
            </Button>
          </CSSTransition>
        )}
      </TransitionGroup>
    </Container>
  );
};

export default Facets;
